<template>
    <v-card flat style="border-top: 1px solid lightgray">
        <v-overlay :value="loadingWorkOrders && !user.id">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-data-table
            class="product-table"
            dense
            :items="workOrders"
            :items-per-page="50"
            :footer-props="footerProps"
            :headers="workOrdersTableHeaders"
            :options.sync="options"
            :server-items-length="totalWorkOrders"
            @update:options="updateOptions"
        >
            <template
                v-for="header in workOrdersTableHeaders"
                v-slot:[`header.${header.value}`]="{ header }"
                :class="'text-' + header.align"
            >
                {{ header.text ? t(header.text) : "" }}
            </template>

            <template v-slot:item="{ item }">
                <tr :key="`workOrder_${item.id}`" @click="handleTableRowClick(item)" class="cursor-pointer">
                    <template v-for="header in workOrdersTableHeaders">
                        <td :key="header.value">
                            <span
                                v-if="header.value === 'status'"
                                :style="{ color: statusColor(item.status) }"
                                class="font-weight-bold"
                            >
                                {{ item.status ? formatStatus(item.status) : "-" }}
                            </span>

                            <span v-else-if="header.value === 'production_date'">
                                <span
                                    class="font-weight-bold"
                                    :style="{ color: dayDiffColor(item.production_date_days_diff) }"
                                >
                                    {{ getProductionDateForDisplay(item) }}
                                </span>
                            </span>

                            <span v-else-if="header.value === 'line_number'">
                                {{ formatLineNumber(item.line_number) }}
                            </span>

                            <span v-else-if="header.value === 'line_workers'">
                                {{ item.line_workers.length ? item.line_workers.join(", ") : "-" }}
                            </span>
                            <span v-else-if="header.value === 'action'">
                                <v-btn
                                    v-if="
                                        user.work_roles &&
                                        (user.work_roles.includes(PermissionsConstants.ROLE_SUPERADMIN) ||
                                            user.work_roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT))
                                    "
                                    icon
                                    @click.stop="handleDrawerIconClick(item.id, item.work_order_group_id)"
                                >
                                    <v-icon>fa-file-contract</v-icon>
                                </v-btn>
                            </span>
                            <span v-else>{{ item[header.value] }}</span>
                        </td>
                    </template>
                </tr>
            </template>
        </v-data-table>

        <div
            v-if="
                (user.work_roles.includes(PermissionsConstants.ROLE_SUPERADMIN) ||
                    user.work_roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT)) &&
                inactiveWorkOrders &&
                inactiveWorkOrders.length > 0
            "
        >
            <v-subheader>{{ t("Inactive Work Orders") }}</v-subheader>
            <v-data-table
                class="product-table"
                dense
                :items="inactiveWorkOrders"
                :headers="inactiveTableHeaders"
                :items-per-page="inactiveWorkOrders.length"
                hide-default-footer
            >
                <template
                    v-for="header in inactiveTableHeaders"
                    v-slot:[`header.${header.value}`]="{ header }"
                    :class="'text-' + header.align"
                >
                    {{ header.text ? t(header.text) : "" }}
                </template>

                <template v-slot:item="{ item }">
                    <tr :key="`workOrder_${item.id}`" @click="handleTableRowClick(item)" class="cursor-pointer">
                        <td>{{ item.id }}</td>
                        <td>{{ item.production_order_number }}</td>
                        <td>{{ formatLineNumber(item.line_number) }}</td>
                        <td class="font-weight-bold" :style="{ color: dayDiffColor(item.production_date_days_diff) }">
                            {{ getProductionDateForDisplay(item) }}
                        </td>
                        <td>{{ item.product_name }}</td>
                        <td class="font-weight-bold" :style="{ color: statusColor(item.status) }">
                            {{ item.status ? formatStatus(item.status) : "-" }}
                        </td>
                        <td class="text-center">
                            <v-btn
                                v-if="
                                    user.work_roles &&
                                    (user.work_roles.includes(PermissionsConstants.ROLE_SUPERADMIN) ||
                                        user.work_roles.includes(PermissionsConstants.ROLE_HEAD_OF_SHIFT))
                                "
                                icon
                                @click.stop="handleDrawerIconClick(item.id, item.work_order_group_id)"
                            >
                                <v-icon>fa-file-contract</v-icon>
                            </v-btn>
                        </td>
                    </tr>
                </template>
            </v-data-table>
        </div>
    </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex";
import WorkOrderGroupView from "@/pages/work-order/work-order-group-view.vue";
import { workOrderMixin } from "@/mixins/work-order-mixin";
import { PermissionsConstants } from "@/permissions";

export default {
    name: "work-order-list",
    components: { WorkOrderGroupView },
    mixins: [workOrderMixin],
    data() {
        return {
            options: {
                page: 1,
                itemsPerPage: 50,
                sortBy: [],
                sortDesc: [],
            },
            footerProps: { "items-per-page-options": [25, 50, 100, 500, 1000] },
        };
    },
    watch: {
        async options(val) {
            await this.fetchWorkOrders({ options: val, search: this.searchWorkOrderString });
        },
    },
    async mounted() {
        if (!this.selectedLineNumberType) {
            await this.$router.push({ name: "work-order-line-number-select" });
        }
    },
    computed: {
        ...mapState(["user"]),
        ...mapState("WorkOrderStore", [
            "workOrders",
            "loadingWorkOrders",
            "activeWorkOrder",
            "totalWorkOrders",
            "workOrdersTableHeaders",
            "inactiveTableHeaders",
            "inactiveWorkOrders",
            "inactiveTableHeaders",
            "selectedLineNumberType",
            "searchWorkOrderString",
        ]),
        PermissionsConstants() {
            return PermissionsConstants;
        },
    },
    methods: {
        ...mapActions("WorkOrderStore", ["fetchWorkOrders"]),
        handleTableRowClick(row) {
            this.$router.push({ name: "work-order-view", params: { workOrderId: row.id } });
        },
        handleDrawerIconClick(itemId, groupId) {
            this.$router.push({
                name: "work-order-group-view",
                params: { workOrderId: itemId, workOrderGroupId: groupId },
            });
        },
        getProductionDateForDisplay(item) {
            return item.production_date_days_diff >= -7
                ? this.formatDayDiff(item.production_date_days_diff)
                : this.formatDate(item.production_date);
        },
        updateOptions(newOptions) {
            this.options = { ...this.options, ...newOptions };
        },
    },
};
</script>

<style scoped>
::v-deep .v-data-table-header th {
    white-space: nowrap;
}

td {
    height: 25px !important;
}

.cursor-pointer {
    cursor: pointer;
}

.worker-list {
    display: flex;
    flex-wrap: wrap;
}

.worker-list span {
    margin-right: 8px;
    font-size: 10px;
}
</style>
